import type { LobbyEvent, PartyJointDecision } from '@danda/shared';
import { notificationService } from '@/lib/notifications';
import type { EventHandlerContext } from './lobby-event-types';

/**
 * Handle decision-related events.
 *
 * Note: Decision state is now managed in-memory on the server (PartyOrchestrator).
 * The frontend only receives notifications; Tavian announces results via party chat.
 *
 * Supports both legacy TypeScript backend and new Rust backend message formats.
 */
// Session-scoped guards for the reconnect vote REPLAY (the server re-sends
// DecisionCreated on every processed join while a vote is open):
// - notified: the "Vote Required" toast/OS notification fires once per
//   decision, not once per reconnect.
// - resolved: a replayed DecisionCreated that raced its own resolution
//   (Resolved delivered first) must not resurrect a dead banner.
const notifiedDecisionIds = new Set<string>();
const resolvedDecisionIds = new Set<string>();

export function handleDecisionEvents(event: LobbyEvent, ctx: EventHandlerContext): boolean {
  const { get } = ctx;
  const state = get();

  switch (event.type) {
    // Legacy TypeScript backend format
    case 'lobby:party-decision-proposed':
      // Persist decision for UI panels (server is source of truth)
      state.setPartyDecision(event.decision);

      // Show notification for the vote - Tavian handles the actual voting via chat
      state.addNotification({
        id: `notif-${Date.now()}`,
        type: 'system',
        title: 'Vote Required',
        message: event.decision.description,
        timestamp: new Date(),
        isRead: false,
        data: { decisionId: event.decision.id, partyId: event.decision.partyId },
      });
      if (typeof window !== 'undefined' && !document.hasFocus()) {
        notificationService.notify('Vote Required', {
          body: event.decision.description,
          type: 'vote',
        });
      }
      return true;

    // Rust backend format: decision created (simplified)
    case 'lobby:decision-created': {
      const { partyId, decisionId, decisionType, description, votesRequired, votesYes, votesNo } =
        event;
      if (resolvedDecisionIds.has(decisionId)) {
        // Reconnect replay raced the resolution; the decision is dead.
        return true;
      }
      const displayDescription = description || `${decisionType} vote`;
      // Create minimal decision structure for UI
      state.setPartyDecision({
        id: decisionId,
        partyId,
        type: decisionType as PartyJointDecision['type'],
        proposedBy: '',
        description: displayDescription,
        status: 'pending',
        votes: [],
        votesRequired,
        metadata: { votesYes, votesNo, votedCharIds: [] },
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 60000).toISOString(), // 1 minute default
      });

      // Show notification (once per decision; reconnect replays skip it)
      if (!notifiedDecisionIds.has(decisionId)) {
        notifiedDecisionIds.add(decisionId);
        state.addNotification({
          id: `notif-${Date.now()}`,
          type: 'system',
          title: 'Vote Required',
          message: `${displayDescription} (${votesYes}/${votesRequired} yes, ${votesNo} no)`,
          timestamp: new Date(),
          isRead: false,
          data: { decisionId, partyId },
        });
        if (typeof window !== 'undefined' && !document.hasFocus()) {
          notificationService.notify('Vote Required', {
            body: displayDescription,
            type: 'vote',
          });
        }
      }
      return true;
    }

    // Legacy TypeScript backend format
    case 'lobby:party-decision-vote': {
      // Merge vote into in-memory decision snapshot (if present)
      const { partyId, vote: cast, decisionId } = event;
      const decision = state.partyDecisions[partyId];
      if (!decision || decision.id !== decisionId || decision.status !== 'pending') {
        return true;
      }
      const alreadyVoted = decision.votes.some((v) => v.characterId === cast.characterId);
      if (alreadyVoted) return true;

      state.updatePartyDecision(partyId, (current) => {
        const base = current ?? decision;
        return {
          ...base,
          votes: [
            ...base.votes,
            { ...cast, timestamp: cast.timestamp ?? new Date().toISOString() },
          ],
        };
      });
      return true;
    }

    // Rust backend format: vote counts updated
    case 'lobby:vote-updated': {
      const { partyId, decisionId, votesYes, votesNo, votedCharIds } = event;
      const decision = state.partyDecisions[partyId];
      if (!decision || decision.id !== decisionId) {
        return true;
      }

      // Update vote counts and voter IDs in the decision
      state.updatePartyDecision(partyId, (current) => {
        if (!current) return decision;
        return {
          ...current,
          metadata: { ...current.metadata, votesYes, votesNo, votedCharIds },
        };
      });
      return true;
    }

    // Legacy TypeScript backend format
    case 'lobby:party-decision-resolved': {
      // Update decision status; UI can show success/fail panels
      state.setPartyDecision(event.decision);
      return true;
    }

    // Rust backend format: decision resolved (simplified)
    case 'lobby:decision-resolved': {
      const { partyId, decisionId, decisionType, description, status } = event;
      resolvedDecisionIds.add(decisionId);
      const displayDescription = description || `${decisionType} vote`;
      // Update existing decision or create resolved state
      state.updatePartyDecision(partyId, (current): PartyJointDecision => {
        const base: PartyJointDecision = current ?? {
          id: decisionId,
          partyId,
          type: decisionType as PartyJointDecision['type'],
          proposedBy: '',
          description: displayDescription,
          status: 'pending',
          votes: [],
          votesRequired: 0,
          metadata: { votesYes: 0, votesNo: 0, votedCharIds: [] },
          createdAt: new Date().toISOString(),
          expiresAt: new Date().toISOString(),
        };
        return {
          ...base,
          description: displayDescription,
          status: status as PartyJointDecision['status'],
        };
      });
      return true;
    }

    default:
      return false;
  }
}
